import React, {useEffect, useState} from 'react';
import styled, {useTheme} from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {tr} from '../../../translation';
import {logIn} from './actions';
import GenericButton from '../../../components/button';
import {Theme} from '../../../theme/lightTheme';
import {
  checkIfEmailValid,
  checkIfFormValid,
  handleChange,
} from '../../../utils/form';
import Paths from '../../../config/paths';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {useSetMainLayoutProps} from '../../../route/routeMainLayout/context';
import TextInput from '../../../components/textInput';
import Text from '../../../components/text';

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const FormContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

export const LoginImageContainer = styled.div`
  width: ${({theme}) => `calc(${theme.text.s1} *2)`};
  aspect-ratio: 1.333;
  align-items: center;
  justify-content: center;
  align-self: center;
  overflow: hidden;
  margin-bottom: 40px;
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;
export const LoginImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const LoginTitle = styled.p`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s4};
  text-align: center;
  margin-bottom: 40px;
  letter-spacing: 1px;
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

export const LoginInputContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
  gap: 16px;
  display: flex;
  flex-direction: column;
`;

export const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  padding-bottom: 16px;
  flex-direction: column;
`;

const ErrorMessage = styled(motion.div)`
  color: ${({theme}) => theme.text.error};
  font-size: ${({theme}) => theme.text.s8};
  text-align: center;
  margin-bottom: 16px;
`;

const ForgetTextContainer = styled.button`
  align-self: flex-end;
  margin-top: 3%;
  background: none;
  border: none;
  cursor: pointer;
`;

const ForgetText = styled.span`
  color: ${({theme}) => theme.primary};
  font-size: ${({theme}) => theme.text.s9};
`;

const ErrorContainer = styled.div`
  min-height: ${({theme}) => theme.text.s10 * 5}px;
`;
const LinkText = styled.a`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s10};
  font-weight: 100;
  text-align: center;
  padding-top: 5%;
  padding-bottom: 5%;
  text-decoration: underline;
  cursor: pointer;
`;

const ErrorText = styled.p`
  color: ${({theme}) => theme.text.error};
  font-size: ${({theme}) => theme.text.s9};
  padding-top: ${({theme}) => theme.spacing.medium}px;
  text-align: center;
`;

const mandatoryFields = ['email', 'password'];

const Login = () => {
  useSetMainLayoutProps({
    showHeader: false,
    showDrawer: false,
    showBackgroundImage: false,
  });

  const theme = useTheme() as Theme;
  const navigate = useNavigate();
  const {currentUser} = useSelector((state: RootState) => state.auth);

  const [form, updateForm] = useState({
    email: currentUser?.email || '',
    password: currentUser?.password || '',
  });
  const [isErrorEmail, setIsErrorEmail] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const isValidEmail = checkIfEmailValid(form?.email);
    setIsErrorEmail(!isValidEmail);
    setFormComplete(checkIfFormValid(form, mandatoryFields) && isValidEmail);
  }, [form]);

  const handleSubmit = async () => {
    setIsError(false);
    setErrorMessage('');

    const res = await logIn({
      email: form.email.toLowerCase().trim(),
      password: form.password,
    });

    if (!res.success) {
      setIsError(true);
      if (res?.message) {
        setErrorMessage(res.message);
      }
    }
  };

  // Function to handle "Forgot Password" button press
  const forgetPressHandler = () => navigate(Paths.auth.forgetPassword);
  const registerPressHandler = () => navigate(Paths.auth.register);

  return (
    <Container>
      <FormContainer
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{duration: 0.4}}>
        <LoginTitle>{tr('login.screenTitle')}</LoginTitle>

        <LoginImageContainer>
          <LoginImage
            src={require('../../../assets/images/onboarding/onboard-1.png')}
          />
        </LoginImageContainer>
        {isError && (
          <ErrorMessage
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.3}}>
            {errorMessage}
          </ErrorMessage>
        )}
        <LoginInputContainer>
          <InputWrapper>
            <TextInput
              label={tr('login.emailLabel')}
              labelStyle={{color: theme.secondary}}
              placeholder={tr('login.email')}
              value={form.email}
              onChange={text =>
                handleChange('email', text.target.value.trim(), updateForm)
              }
              showRightIcon={form.email.length > 0 && !isErrorEmail}
              rightIconSize={theme.text.s6}
              rightIconName={'checkCircle'}
              showLeftIcon
              leftIconName={'user'}
              leftIconColor={theme.text.dark}
            />
          </InputWrapper>
          <InputWrapper>
            <TextInput
              label={tr('login.passwordLabel')}
              labelStyle={{color: theme.secondary}}
              placeholder={tr('login.password')}
              value={form.password}
              onChange={text =>
                handleChange('password', text.target.value, updateForm)
              }
              secureInput
              showLeftIcon
              leftIconName={'lock'}
              leftIconColor={theme.text.dark}
            />
            <ForgetTextContainer onClick={forgetPressHandler}>
              <ForgetText>{tr('login.forget')}</ForgetText>
            </ForgetTextContainer>
          </InputWrapper>

          <ErrorContainer>
            {isError && (
              <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}>
                <ErrorText>{errorMessage || tr('login.error')}</ErrorText>
              </motion.div>
            )}
          </ErrorContainer>
        </LoginInputContainer>

        <GenericButton
          onPress={handleSubmit}
          disabled={!formComplete || isErrorEmail}
          label={tr('login.signIn')}
          style={{
            width: '100%',
            opacity: !formComplete || isErrorEmail ? 0.7 : 1,
            marginBottom: '5%',
          }}
        />
        <Text
          style={{
            color: theme.text.dark,
            fontSize: theme.text.s8,
            marginTop: '5%',
          }}>
          {tr('login.registerPart1')}{' '}
          <LinkText
            onClick={registerPressHandler}
            style={{
              fontSize: theme.text.s8,
              fontWeight: 'bold',
            }}>
            {tr('login.registerPart2')}
          </LinkText>
        </Text>
      </FormContainer>
    </Container>
  );
};

export default Login;
